import { Checkbox, Grid, ListItemText, MenuItem, Select } from "@mui/material";
import { Button } from "components/Buttons/Button";
import { createRef, memo } from "react";
import { ButtonType, ButtonColorScheme } from "types/ui";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { CircularProgress } from "@material-ui/core";

type Props = {
  options: string[];
  selectedOptions: string[];
  hint?: string;
  menuTitle?: string;
  menuSubTitle?: string;
  leftButtonName?: string;
  rightButtonName?: string;
  onLeftButtonClicked?: any;
  onRightButtonClicked?: any;
  onItemSelected?: any;
  onSelectOpened?: any;
  displayIcon?: boolean;
  width?: number;
  height?: number;
  paddingTop?: number;
  style?: any;
  customRenderValue?: string;
  emptyMessage?: string;
  isLoading?: boolean;
  listItemStyle?: React.CSSProperties;
};

export const MultiSelect = memo(
  ({
    options,
    selectedOptions,
    hint,
    customRenderValue,
    menuTitle,
    menuSubTitle,
    leftButtonName,
    rightButtonName,
    onLeftButtonClicked,
    onRightButtonClicked,
    onItemSelected,
    onSelectOpened,
    displayIcon = true,
    width,
    height,
    paddingTop,
    style,
    emptyMessage,
    isLoading,
    listItemStyle,
  }: Props) => {
    const itemRef = createRef();

    const ITEM_HEIGHT = height ?? 100;
    const ITEM_PADDING_TOP = paddingTop ?? 8;
    const MenuProps = {
      PaperProps: {
        style: {
          maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
          width: width ?? 250,
          maxWidth: 250,
          borderRadius: 10,
        },
      },
    };

    const clickElement = (ref: React.RefObject<any>) => {
      ref.current.dispatchEvent(
        new MouseEvent("click", {
          view: window,
          bubbles: true,
          cancelable: true,
          buttons: 1,
        })
      );
    };

    const Icon = () => (displayIcon ? <ArrowDropDownIcon /> : null);

    return (
      <Select
        displayEmpty
        className="multi-select"
        ref={itemRef}
        id="multiple-checkbox"
        onClick={(e) => {
          e.stopPropagation();
        }}
        onOpen={() => {
          if (onSelectOpened) onSelectOpened();
        }}
        sx={style ?? null}
        IconComponent={() => <Icon />}
        multiple
        value={[hint ?? ""]}
        renderValue={(selected) => {
          if (customRenderValue) {
            return customRenderValue;
          }
          return selected.join(", ");
        }}
        MenuProps={MenuProps}
      >
        <Grid
          md={12}
          sx={{
            ml: 3,
            mb: 1,
            fontSize: 14,
            color: "#231F20",
            fontWeight: "bold",
          }}
        >
          {menuTitle ?? ""}
        </Grid>
        <Grid
          md={12}
          sx={{ ml: 3, fontSize: 14, color: "#5C5D68", fontWeight: "500" }}
        >
          {menuSubTitle ?? ""}
        </Grid>
        <Grid md={12} sx={{ ml: 0, mt: 1, mb: 1 }}>
          {options.length > 0 ? (
            options.map((name) => (
              <MenuItem
                key={name}
                value={name}
                disableRipple
                sx={{ width: "100%", borderBottom: "1px solid #eee" }}
              >
                <Checkbox
                  checked={selectedOptions.indexOf(name) > -1}
                  onChange={(e) => onItemSelected(e, name)}
                />
                <ListItemText
                  primary={name}
                  primaryTypographyProps={{
                    style: listItemStyle ?? { whiteSpace: "normal" },
                  }}
                />
              </MenuItem>
            ))
          ) : (
            <Grid
              sx={{
                mt: 3,
                mb: 3,
                fontSize: 12,
                textAlign: "center",
                color: "#5C5D68",
              }}
            >
              {emptyMessage}
            </Grid>
          )}
        </Grid>
        <Grid container>
          <Grid container md={6} justifyContent="center" textAlign="center">
            <Button
              title={leftButtonName ?? ""}
              onClick={onLeftButtonClicked}
              type={ButtonType.text}
              shadowed={selectedOptions.length > 0}
              style={{ fontSize: 14, color: "#9D9EA5" }}
            />
          </Grid>
          <Grid container md={6} justifyContent="center" alignItems="center">
            {isLoading ? (
              <CircularProgress color="inherit" size={15} />
            ) : (
              rightButtonName && (
                <Button
                  title={rightButtonName ?? ""}
                  onClick={() => {
                    onRightButtonClicked();
                    clickElement(itemRef);
                  }}
                  type={ButtonType.small}
                  colorScheme={ButtonColorScheme.gradient}
                />
              )
            )}
          </Grid>
        </Grid>
      </Select>
    );
  }
);
